import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, Star, Target, Gem } from "lucide-react";
import { BrandHeart, BrandDelivery, BrandReturn, BrandBag, BrandRoomPlan, BrandProductFacts, BrandPrice, BrandReview, BrandSearch, BrandChair, BrandRoomLove, BrandDeliveryWindow, BrandProtectedParcel } from "@/components/BrandIcons";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

import { WebSiteJsonLd, LocalBusinessJsonLd } from "@/components/JsonLd";
import { OGTags } from "@/components/OGTags";
import { getSupabase } from "@/integrations/supabase/lazy";

import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { HomeProducts } from "@/components/HomeProducts";
import { ShopByPrice } from "@/components/ShopByPrice";
import { RoomMotionCard } from "@/components/RoomMotionCard";
import { Reveal } from "@/components/Reveal";
import { EverydayFinds } from "@/components/EverydayFinds";


import heroAsset from "@/assets/home2/hero.jpg.asset.json";
import roomDiningAsset from "@/assets/home2/room-dining.jpg.asset.json";
import roomLiving from "@/assets/home2/room-living.jpg";
import roomBedroom from "@/assets/home2/room-bedroom.jpg";
import roomOffice from "@/assets/home2/room-office.jpg";
import roomOutdoor from "@/assets/home2/room-outdoor.jpg";
// Re-encoded for the web (the originals were 20-40 MB each at 17-33 Mbps).
const heroVideo = "/video/hero.mp4";
const heroPoster = heroAsset.url;
// Phones show only the middle of the 1920×1080 poster (object-cover in a tall
// box): a centre crop as WebP is 62 KB instead of 253 KB, and it is the
// homepage's LCP image on mobile (preloaded by scripts/prerender-og.mjs).
const heroPosterMobile = "/images/hero-mobile.webp";
const roomDining = roomDiningAsset.url;
import sitHappensAsset from "@/assets/home2/sit-happens.jpg.asset.json";
// Resized copies of home2/look-*.jpg (700×700, 36–102 KB each): 400 px for the
// Design Inspiration tiles and review thumbs, 84 px for the 24–28 px hero
// avatars, which alone cost 231 KB in the first seconds of the phone homepage.
import look1 from "@/assets/thumbs/look-1-400.webp";
import look2 from "@/assets/thumbs/look-2-400.webp";
import look3 from "@/assets/thumbs/look-3-400.webp";
import look4 from "@/assets/thumbs/look-4-400.webp";
import look5 from "@/assets/thumbs/look-5-400.webp";
import look6 from "@/assets/thumbs/look-6-400.webp";
import avatar1 from "@/assets/thumbs/look-1-avatar.webp";
import avatar2 from "@/assets/thumbs/look-2-avatar.webp";
import avatar3 from "@/assets/thumbs/look-3-avatar.webp";
import avatar4 from "@/assets/thumbs/look-4-avatar.webp";
import newsletterBg from "@/assets/home2/newsletter-bg.jpg";
import vaseDecorAsset from "@/assets/home2/vase-decor.jpg.asset.json";
import { FREE_SHIPPING_OVER, FREE_SHIPPING_THRESHOLD, RETURN_SHIPPING_COPY, DELIVERY_WINDOW_COPY } from "@/lib/shipping";
// The H1 text, shared with the prerendered homepage body.
import { HOME_H1 } from "../../scripts/site-identity.mjs";
import { toast } from "sonner";
import { trackLead } from "@/lib/analytics";
import { rememberEmailSignup } from '@/lib/emailAttribution';
const sitHappens = sitHappensAsset.url;
const sitHappensVideo = "/video/sit-happens-loop.mp4";
const vaseDecor = vaseDecorAsset.url;
const sitHappensMainVideo = "/video/sit-happens-main.mp4";

// --- Tunables for syncing the page expansion with the in-video expansion ---
const EXPAND_START = 0.0;
const EXPAND_END   = 2.2;
const STOP_AT      = 7.0;
const COLLAPSE_BACK_AT = 6.4;

// Easing — matches the in-video motion feel
const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

const SitHappensSection = () => {
  const reducedMotion = useReducedMotion();
  const [viewportWidth, setViewportWidth] = useState(() => typeof window !== "undefined" ? window.innerWidth : 1280);
  const IS_MOBILE = viewportWidth < 768;
  // Reserve room for the mobile caption and play control even on narrow phones.
  const COLLAPSED_H = IS_MOBILE ? Math.max(240, Math.round(viewportWidth * 0.42)) : 260;
  const EXPANDED_H = IS_MOBILE ? Math.max(240, Math.round(viewportWidth * 9 / 16)) : 720;
  const INITIAL_SCALE = IS_MOBILE ? 1 : 1.08;
  useEffect(() => {
    const resize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", resize, { passive: true });
    return () => window.removeEventListener("resize", resize);
  }, []);
  const loopRef = useRef<HTMLVideoElement>(null);
  const mainRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  // The section sits far below the fold: its 366 KB still (also both videos'
  // poster) loaded at high priority with the hero, slowing the hero on phones.
  const [near, setNear] = useState(false);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setNear(true);
        io.disconnect();
      }
    }, { rootMargin: "800px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  // progress 0 → 1 driving the synced expansion
  const progressRef = useRef(0);
  const rafRef = useRef<number>(0);

  // Boomerang loop: play forward, on ended play reverse via rAF, then forward again.
  useEffect(() => {
    const v = loopRef.current;
    if (!v) return;
    if (reducedMotion) { v.pause(); return; }
    let raf = 0;
    let lastTs = 0;

    const reverseTick = (ts: number) => {
      if (!lastTs) lastTs = ts;
      const dt = (ts - lastTs) / 1000;
      lastTs = ts;
      const next = v.currentTime - dt;
      if (next <= 0) {
        v.currentTime = 0;
        lastTs = 0;
        v.play().catch(() => {});
        return;
      }
      v.currentTime = next;
      raf = requestAnimationFrame(reverseTick);
    };

    const onEnded = () => {
      lastTs = 0;
      v.pause();
      raf = requestAnimationFrame(reverseTick);
    };

    v.loop = false;
    v.muted = true;
    v.addEventListener("ended", onEnded);
    // Start (and so download) the loop only when the section is about to be
    // seen: it sits well below the fold, and on a phone most visitors never
    // scroll this far.
    let started = false;
    const io = new IntersectionObserver((entries) => {
      if (started || !entries.some((e) => e.isIntersecting)) return;
      started = true;
      io.disconnect();
      v.preload = "auto";
      v.play().catch(() => {});
    }, { rootMargin: "300px 0px" });
    io.observe(v);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
      v.removeEventListener("ended", onEnded);
    };
  }, [reducedMotion]);

  // Apply synced expansion: container height + counter-zoom on the video, so the
  // visible "inner frame" (what's inside the black borders) stays the same physical
  // size, and the borders never appear. Then both grow together to full size.
  const applyProgress = (p: number) => {
    progressRef.current = p;
    const eased = reducedMotion ? 0 : easeInOut(Math.max(0, Math.min(1, p)));
    const h = COLLAPSED_H + (EXPANDED_H - COLLAPSED_H) * eased;
    if (containerRef.current) containerRef.current.style.height = `${h}px`;
    if (IS_MOBILE) {
      // On mobile keep native 16:9 videos vertically centered — height is driven by container clip only.
      [loopRef.current, mainRef.current].forEach((video) => {
        if (!video) return;
        video.style.height = `${EXPANDED_H}px`;
        video.style.top = "50%";
        video.style.bottom = "auto";
        video.style.transform = "translateY(-50%)";
        video.style.transformOrigin = "center center";
      });
      return;
    }
    if (mainRef.current) {
      const maxScale = INITIAL_SCALE;
      const scale = maxScale - (maxScale - 1) * eased;
      mainRef.current.style.transform = `scale(${scale})`;
      mainRef.current.style.transformOrigin = "center center";
    }
  };


  // Reset to collapsed visual state
  useEffect(() => {
    applyProgress(progressRef.current);
  }, [viewportWidth, reducedMotion]);

  // rAF loop while playing — drives the synced animation from the video's currentTime
  useEffect(() => {
    if (!playing) return;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const v = mainRef.current;
      if (v) {
        const t = v.currentTime;
        let p: number;
        if (t < EXPAND_START) p = 0;
        else if (t >= EXPAND_END && t < COLLAPSE_BACK_AT) p = 1;
        else if (t >= COLLAPSE_BACK_AT && t < STOP_AT) {
          // gentle shrink back during the final 0.6s
          const k = (t - COLLAPSE_BACK_AT) / (STOP_AT - COLLAPSE_BACK_AT);
          p = 1 - k;
        } else if (t >= STOP_AT) p = 0;
        else p = (t - EXPAND_START) / (EXPAND_END - EXPAND_START);
        applyProgress(p);

        if (t >= STOP_AT) {
          v.pause();
          setPlaying(false);
          setTimeout(() => {
            if (mainRef.current) mainRef.current.currentTime = 0;
            applyProgress(0);
          }, 200);
          return;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafRef.current);
    };
  }, [playing, viewportWidth, reducedMotion]);

  const handlePlay = async () => {
    const v = mainRef.current;
    if (!v) return;
    v.currentTime = 0;
    v.muted = false;
    applyProgress(0);
    setPlaying(true);
    try { await v.play(); } catch { setPlaying(false); applyProgress(0); }
  };

  return (
    <section className="relative bg-sand">
      <div
        ref={containerRef}
        className="relative w-full max-w-[100vw] overflow-hidden bg-foreground"
        style={{ height: `${COLLAPSED_H}px` }}
      >

        {/* Background still */}
        <img
          src={near ? sitHappens : undefined}
          alt=""
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover md:object-cover object-center"
        />
        {/* Loop video (boomerang) — visible when not playing */}
        <video
          ref={loopRef}
          src={sitHappensVideo}
          playsInline
          muted
          preload="none"
          poster={near ? sitHappens : undefined}
          controls={false}
          style={IS_MOBILE ? { height: `${EXPANDED_H}px`, top: "50%", bottom: "auto", transform: "translateY(-50%)" } : undefined}
          className={`absolute left-0 top-0 h-full w-full object-cover object-center z-10 transition-opacity duration-500 ${playing ? "opacity-0" : "opacity-100"}`}
        />
        {/* Main video — on mobile it stays full-width at 16:9 while the container clips/reveals height (no scale zoom). */}
        <video
          ref={mainRef}
          src={sitHappensMainVideo}
          playsInline
          preload="none"
          poster={near ? sitHappens : undefined}
          controls={false}
          style={IS_MOBILE ? { height: `${EXPANDED_H}px`, top: "50%", bottom: "auto", transform: "translateY(-50%)" } : undefined}
          className={`absolute left-0 top-0 h-full w-full object-cover object-center z-20 will-change-transform ${playing ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        />


        {/* Desktop left scrim */}
        {!playing && <div className="hidden md:block absolute inset-y-0 left-0 z-20 w-[36%] bg-gradient-to-r from-foreground/85 via-foreground/50 to-transparent" />}

        {/* Desktop play CTA cluster */}
        {!playing && (
          <div className="pointer-events-none absolute inset-0 z-30 hidden md:flex items-center justify-center">
            {/* Soft vignette behind the caption + play button only, so the type stays
                legible without darkening the whole frame */}
            <div
              aria-hidden
              className="absolute left-1/2 top-1/2 h-[420px] w-[900px] -translate-x-1/2 -translate-y-1/2"
              style={{
                background:
                  "radial-gradient(ellipse at center, hsl(var(--foreground) / 0.62) 0%, hsl(var(--foreground) / 0.42) 45%, hsl(var(--foreground) / 0.14) 70%, transparent 85%)",
              }}
            />
            <div className="relative flex items-center gap-5 drop-shadow-[0_3px_10px_hsl(var(--foreground)/0.9)]">

              <div className="flex flex-col gap-3 text-right">
                <div className="flex items-center gap-3 justify-end">
                  <span className="font-display italic text-background text-[40px] leading-none whitespace-nowrap">See the space</span>
                  <svg width="70" height="34" viewBox="0 0 70 34" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-background">
                    <path d="M2 4 C 28 4, 50 14, 66 30" />
                    <path d="M66 30 L 56 28 M66 30 L 62 20" />
                  </svg>
                </div>
                <div className="flex items-center gap-3 justify-end">
                  <span className="font-display italic text-background text-[40px] leading-none whitespace-nowrap">come to life</span>
                  <svg width="70" height="34" viewBox="0 0 70 34" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-background">
                    <path d="M2 30 C 28 30, 50 20, 66 4" />
                    <path d="M66 4 L 56 6 M66 4 L 62 14" />
                  </svg>
                </div>
              </div>
              <Button variant="ghost" size="icon"
                type="button"
                onClick={handlePlay}
                className="pointer-events-auto flex h-[78px] w-[78px] items-center justify-center rounded-full bg-background/95 text-foreground shadow-[0_10px_28px_hsl(var(--foreground)/0.35)] hover:scale-105 transition-transform"
                aria-label="Play video"
              >
                <span className="ml-1 h-0 w-0 border-y-[11px] border-y-transparent border-l-[18px] border-l-foreground" />
              </Button>
            </div>
          </div>
        )}

        {/* Mobile play button + title overlay — centered on the loop video */}
        {!playing && (
          <div className="absolute inset-0 z-30 flex md:hidden items-center justify-center">
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/85 via-foreground/40 to-foreground/60" />
            <div className="relative flex flex-col items-center gap-4 px-6 text-center [text-shadow:0_2px_14px_hsl(var(--foreground)/0.9)]">
              <p className="text-[10px] tracking-[0.28em] uppercase font-bold text-primary">Design that feels right</p>
              <h2 className="font-display text-[42px] leading-none text-background">
                Sit <em className="italic text-primary font-normal">happens.</em>
              </h2>
              <Button variant="ghost" size="icon"
                type="button"
                onClick={handlePlay}
                className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-background/95 text-foreground shadow-[0_10px_28px_hsl(var(--foreground)/0.45)] active:scale-95 transition-transform"
                aria-label="Play video"
              >
                <span className="ml-1 h-0 w-0 border-y-[12px] border-y-transparent border-l-[18px] border-l-foreground" />
              </Button>
              <p className="text-[12px] text-background/95 max-w-[240px]">Comfort that completes the room.</p>
            </div>
          </div>
        )}

        {/* Desktop left text column (hidden on mobile — replaced by caption below) */}
        <div className={`pointer-events-none absolute inset-0 z-20 hidden md:flex max-w-none mx-0 px-[6%] py-6 items-center drop-shadow-[0_4px_14px_hsl(var(--foreground)/0.95)] transition-opacity duration-500 ${playing ? "opacity-0" : "opacity-100"}`}>
          <div>
            <p className="text-[10px] tracking-[0.24em] uppercase text-background/90 mb-2">Design that feels right</p>
            <h2 className="font-display text-[38px] leading-none text-background whitespace-nowrap">Sit happens.</h2>
            <div className="h-px w-11 bg-background my-3" />
            <p className="text-[12px] text-background/95 leading-snug max-w-[200px] mb-3">Comfort that completes the room.</p>
            <Link to="/category/living" className="pointer-events-auto inline-flex h-10 items-center gap-3 border border-background text-background bg-background/10 backdrop-blur-sm rounded-md px-5 text-[10px] tracking-[0.16em] uppercase font-bold hover:bg-background hover:text-foreground transition-colors">
              Shop Living Room <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile caption — compact single row under the video */}
      <Link to="/category/living" className="md:hidden w-[89%] max-w-[1140px] mx-auto py-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-[24px] leading-none text-foreground">Sit happens.</h2>
          <p className="mt-0.5 text-[12px] text-foreground/70">Comfort that completes the room.</p>
        </div>
        <span className="shrink-0 h-11 w-11 rounded-full bg-foreground text-background flex items-center justify-center"><ArrowRight className="h-4 w-4" /></span>
      </Link>
    </section>
  );
};



const rooms = [
  { label: "Living Room", href: "/category/living", img: roomLiving },
  { label: "Bedroom", href: "/category/bed-bath", img: roomBedroom },
  { label: "Dining Room", href: "/category/dining", img: roomDining },
  { label: "Home Office", href: "/category/office", img: roomOffice },
  { label: "Outdoor", href: "/category/outdoor", img: roomOutdoor },
];

const looks = [
  { img: look1, href: "/category/living", alt: "Modern living room styled with neutral sofa and accent decor" },
  { img: look2, href: "/category/living", alt: "Cozy living room with layered textures and warm lighting" },
  { img: look3, href: "/category/bed-bath", alt: "Serene bedroom styled with linen bedding and natural tones" },
  { img: look4, href: "/category/dining", alt: "Dining room styled with wood table and upholstered chairs" },
  { img: look5, href: "/category/outdoor", alt: "Outdoor patio styled with woven lounge seating" },
  { img: look6, href: "/ideas", alt: "Editorial interior design ideas and inspiration" },
];

const trustItems = [
  { icon: BrandHeart, title: "Curated Quality", sub: "Handpicked pieces", href: "/about#approach" },
  { icon: BrandDelivery, title: "Free Shipping", sub: FREE_SHIPPING_OVER, href: "/shipping" },
  { icon: BrandReturn, title: "30-Day Returns", sub: "30 days from delivery", href: "/returns" },
  { icon: BrandBag, title: "Secure Checkout", sub: "Options shown at checkout", href: "/faq#payments" },
  { icon: BrandRoomPlan, title: "Free Room Plan", sub: "Tell us about your room", href: "/design-services" },
];

const whyItems = [
  { icon: BrandProductFacts, title: "Plain Product Facts", sub: "Missing data left out, not guessed", href: "/about#approach" },
  { icon: BrandRoomPlan, title: "Thoughtful Design", sub: "Made for living", href: "/about#approach" },
  { icon: BrandPrice, title: "Honest Prices", sub: "No invented \"was\" prices", href: "/about#approach" },
  { icon: BrandProductFacts, title: "Terms Stated Upfront", sub: "Shipping and returns in plain words", href: "/returns" },
  { icon: BrandReview, title: "Real Reviews Only", sub: "We never write our own", href: "/about#approach" },
];

const howSteps = [
  { n: "01", icon: BrandSearch, title: "Discover", sub: "Explore collections and find your style", href: "/categories" },
  { n: "02", icon: BrandChair, title: "Personalize", sub: "Choose pieces that fit your space", href: "/design-your-room" },
  { n: "03", icon: BrandDelivery, title: "Delivered", sub: "4–10 business days in the U.S.", href: "/shipping" },
  { n: "04", icon: BrandRoomLove, title: "Love Your Space", sub: "Thoughtfully designed for real life", href: "/ideas" },
];

// Testimonials come from the real reviews table only — never placeholders.
const reviewImages = [look1, look4, look3];
const heroAvatars = [avatar1, avatar2, avatar3, avatar4];

interface HomeReview {
  id: string;
  quote: string;
  name: string;
  rating: number;
  handle: string;
}

function useRealHomeReviews(): HomeReview[] {
  const [reviews, setReviews] = useState<HomeReview[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const supabase = await getSupabase();
      const { data } = await supabase
        .from("reviews")
        .select("id, body, author_name, rating, product_handle")
        .gte("rating", 4)
        .order("helpful_count", { ascending: false })
        .limit(3);
      if (cancelled || !data) return;
      setReviews(
        data.map((r) => ({
          id: r.id,
          quote: r.body,
          name: r.author_name,
          rating: r.rating,
          handle: r.product_handle,
        }))
      );
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return reviews;
}

const promiseItems = [
  { icon: BrandDeliveryWindow, title: "Clear delivery windows", sub: `${DELIVERY_WINDOW_COPY}.`, href: "/shipping" },
  { icon: BrandProtectedParcel, title: "If it arrives damaged", sub: "Tell us right away, ideally within 48 hours, with photos. We cover return shipping and arrange a replacement or refund.", href: "/returns" },
  { icon: BrandRoomPlan, title: "Real design help", sub: "Stuck on layout or color? Ask for a free room plan before you buy.", href: "/design-services" },
];

const homeFaqs = [
  { q: "How fast will my order arrive?", a: `Delivery takes ${DELIVERY_WINDOW_COPY}.`, href: "/faq#shipping" },
  { q: "What if something arrives damaged?", a: "Contact us right away — ideally within 48 hours of delivery — with photos. We cover return shipping and arrange a replacement or refund.", href: "/faq#returns" },
  { q: "Can I return it if it doesn't fit my space?", a: `Yes — 30 days from delivery. ${RETURN_SHIPPING_COPY} Items must be unused and in their original packaging.`, href: "/faq#returns" },
  { q: "Is assembly required?", a: "It depends on the piece — larger furniture usually needs some assembly. Where the supplier states assembly details, they are on the product page.", href: "/faq" },
  { q: "Do you offer financing?", a: "LoftLoom doesn't offer financing or instalment plans of its own. Payment is taken at checkout; a pay-over-time option shown there (Lay-Buy) is run by a third party under its own terms.", href: "/faq#payments" },
  { q: "Do you help with picking pieces?", a: "Yes — ask for a free room plan and we'll reply by email with a layout and pieces from the catalogue.", href: "/design-services" },
];


const SectionTitle = ({ title, href, action }: { title: string; href: string; action: string }) => (
  <div className="flex items-end justify-between gap-3 mb-6 md:mb-10 border-b border-border/60 pb-3 md:pb-4">
    <h2 className="min-w-0 font-display text-[24px] md:text-[38px] leading-tight text-foreground">{title}</h2>
    <Link to={href} className="min-h-11 text-[10px] tracking-[0.12em] md:tracking-[0.22em] uppercase font-semibold text-foreground/70 hover:text-primary inline-flex items-center gap-1.5 whitespace-nowrap shrink-0 pb-1">
      <span className="hidden sm:inline">{action}</span><span className="sm:hidden">View all</span> <ArrowRight className="h-3 w-3" />
    </Link>
  </div>
);


const Index = () => {
  const reducedMotion = useReducedMotion();
  const realReviews = useRealHomeReviews();
  // The hero has separate mobile and desktop layouts, one hidden by CSS. Give
  // only the visible one a src, or every visitor downloads the video twice.
  // Read synchronously so the hidden one never starts loading on first render.
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches
  );
  // Phones: the 1.6 MB hero video downloaded alongside the 62 KB still that
  // is the page's LCP, which on slow 4G pushed the still to ~12 s. The video
  // now starts after the page has loaded, and not at all on 2G/3G or with
  // data saver on; the still stays as the hero either way.
  const [heroVideoOk, setHeroVideoOk] = useState(false);
  useEffect(() => {
    const conn = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
    if (conn?.saveData || /(^|-)2g$|^3g$/.test(conn?.effectiveType || "")) return;
    let timer = 0;
    const go = () => {
      timer = window.setTimeout(() => setHeroVideoOk(true), 1500);
    };
    if (document.readyState === "complete") go();
    else window.addEventListener("load", go, { once: true });
    return () => {
      window.removeEventListener("load", go);
      window.clearTimeout(timer);
    };
  }, []);
  useEffect(() => {
    const mql = window.matchMedia("(min-width: 768px)");
    const onChange = () => setIsDesktop(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  // Exactly one H1 in the document. It used to sit only in the desktop hero,
  // which is display:none on phones, so the phone homepage had no heading.
  const MobileHeading = isDesktop ? "p" : "h1";
  const DesktopHeading = isDesktop ? "h1" : "p";
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterBusy, setNewsletterBusy] = useState(false);
  // Saves the signup only. No welcome email is sent from here.
  const handleNewsletter = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = newsletterEmail.trim();
    if (!email || newsletterBusy) return;
    setNewsletterBusy(true);
    const supabase = await getSupabase();
    const { error } = await supabase.from("newsletter_subscribers").insert({ email, source: "homepage" });
    setNewsletterBusy(false);
    if (error && error.code !== "23505") {
      toast.error("Couldn't subscribe you. Please try again.", { position: "top-center" });
      return;
    }
    if (!error) { trackLead("newsletter_homepage"); void rememberEmailSignup(email, 'homepage').catch(() => undefined); }
    setNewsletterEmail("");
    toast.success(error ? "You're already subscribed." : "You're subscribed.", { position: "top-center" });
  };
  return (
    <div className="min-h-screen bg-background text-foreground">
      <OGTags
        title="Modern Loft Furniture — Sofas, Dining & Bedroom | LoftLoom"
        description={`Shop curated modern furniture for loft living: sofas, sectionals, dining, bedroom and lighting. Free U.S. shipping over $${FREE_SHIPPING_THRESHOLD} and 30-day returns.`}
        url="https://loftloom.com/"
      />
      <WebSiteJsonLd name="LoftLoom" url="https://loftloom.com" />
      <LocalBusinessJsonLd />
      <Header />

      <main>
        <section className="relative bg-sand">
          {/* Mobile: full-bleed hero — video extends under the transparent header */}
          <div className="md:hidden -mt-[60px] relative w-full h-[62vh] min-h-[420px] max-h-[560px] overflow-hidden">
            <video
              src={isDesktop || !heroVideoOk || reducedMotion ? undefined : heroVideo}
              poster={isDesktop ? undefined : heroPosterMobile}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              aria-label="Curated modern living room with cream sectional, arched shelf and garden view"
              className="absolute inset-0 w-full h-full object-cover"
            />
            {!isDesktop && <img src={heroPosterMobile} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover -z-10" />}
            <div className="absolute inset-x-0 top-0 h-[140px] bg-gradient-to-b from-foreground/70 via-foreground/25 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 h-[78%] bg-gradient-to-t from-foreground via-foreground/85 via-40% to-transparent" />

            <div className="absolute inset-x-0 bottom-0 w-[89%] max-w-[1140px] mx-auto pb-6 pt-6 text-background [text-shadow:0_2px_12px_hsl(var(--foreground)/0.9)]">
              {/* font-sans: headings default to the display face, this line is set in sans. */}
              <MobileHeading className="font-sans text-[10px] tracking-[0.24em] uppercase font-semibold text-background/90 mb-2">{HOME_H1}</MobileHeading>
              <p className="font-display text-[52px] leading-[0.86] tracking-tight text-background">
                Live with <em className="italic text-primary font-normal">intention.</em>
              </p>
              <p className="mt-3 text-[13px] leading-relaxed text-background max-w-[320px]">Curated furniture &amp; décor — no guesswork.</p>
              <div className="mt-4 flex flex-col items-start gap-2">
                <Link to="/categories" className="group inline-flex h-12 items-center gap-4 bg-primary text-primary-foreground rounded-md px-6 text-[11px] tracking-[0.16em] uppercase font-bold hover:bg-primary/90 transition-all duration-200 shadow-[0_10px_30px_hsl(var(--foreground)/0.4)] cursor-pointer">
                  Explore Collections <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/design-services" className="min-h-11 inline-flex items-center gap-1.5 text-[11px] tracking-[0.16em] uppercase font-bold text-background/95 underline underline-offset-4 decoration-background/40 hover:decoration-background transition">
                  Get a free room plan <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
              <Link to="/about#stats" className="mt-4 flex items-center gap-2.5 hover:opacity-80 transition-opacity duration-200 cursor-pointer">
                <div className="flex -space-x-1.5 shrink-0">
                  {heroAvatars.map((img, i) => (
                    <img key={i} src={img} alt="" width={28} height={28} decoding="async" className="h-7 w-7 rounded-full object-cover border-2 border-background" />
                  ))}
                </div>
                <span className="text-[12px] text-background/95">Designed for real homes</span>
              </Link>
            </div>
          </div>





          {/* Desktop: overlay layout */}
          <div className="hidden md:block">
            <video
              src={isDesktop && !reducedMotion ? heroVideo : undefined}
              // Each hero loads only its own poster: hidden by CSS, the other
              // one still downloaded (253 KB on phones).
              poster={isDesktop ? heroPoster : undefined}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              aria-label="Curated modern living room with cream sectional, arched shelf and garden view"
              className="w-full h-[500px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-sand from-0% via-sand/90 via-30% to-transparent to-55%" />
            <div className="absolute inset-0">
              <div className="w-[89%] max-w-[1140px] mx-auto h-full flex items-center">
                <div className="max-w-[360px] pt-1">
                  <DesktopHeading className="font-display text-[10px] tracking-[0.22em] uppercase font-medium text-foreground/70 mb-3">{HOME_H1}</DesktopHeading>
                  <div aria-hidden="true" className="font-display text-[76px] leading-[0.88] tracking-tight text-foreground">
                    Live with<br />
                    <em className="italic text-primary font-normal">intention.</em>
                  </div>
                  <p className="mt-3 text-[10px] tracking-[0.18em] uppercase font-semibold text-foreground/75">Modern furniture &amp; décor for lofts, apartments &amp; urban homes.</p>
                  <p className="mt-3 text-[13px] leading-relaxed text-foreground max-w-[300px]">Made for homeowners who want a stylish space without the guesswork.</p>
                  <div className="mt-4 flex flex-wrap items-center gap-2.5">
                    <Link to="/categories" className="group inline-flex h-10 items-center gap-5 bg-primary text-primary-foreground rounded-md px-5 text-[10px] tracking-[0.12em] uppercase font-bold hover:bg-primary/90 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 cursor-pointer">
                      Explore Collections <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                    <Link to="/design-services" className="group inline-flex h-10 items-center gap-3 border border-foreground/30 text-foreground rounded-md px-4 text-[10px] tracking-[0.12em] uppercase font-bold hover:bg-foreground hover:text-background hover:-translate-y-0.5 transition-all duration-200 cursor-pointer">
                      Get a Free Room Plan <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                  <Link to="/about#stats" className="mt-4 flex items-center gap-3 hover:opacity-80 transition-opacity duration-200 cursor-pointer">
                    <div className="flex -space-x-1.5">
                      {heroAvatars.map((img, i) => (
                        <img key={i} src={img} alt="" width={24} height={24} decoding="async" className="h-6 w-6 rounded-full object-cover border-2 border-background" />
                      ))}
                    </div>
                    <span className="text-[11px] text-foreground/80">Designed for real homes</span>
                  </Link>
                </div>
              </div>
            </div>
            {/* Circular "Curated for Real Living" badge removed per audit (H-1/R1) —
                trust signal moved to the star-rating row beside the avatar stack. */}
          </div>
        </section>

        <section className="bg-sand pb-3 md:pb-3">
          {/* Trust strip: horizontal snap row on mobile, 5-col grid desktop */}
          <div className="md:hidden -mt-3 relative z-10">
            <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide pl-[5.5%] pr-[5.5%]">
              {trustItems.map((item) => (
                <Link
                  key={item.title}
                  to={item.href}
                  className="group shrink-0 w-[58%] snap-start bg-background rounded-md shadow-md border border-border/70 flex items-center gap-2.5 px-3 py-3 hover:shadow-lg hover:-translate-y-0.5 hover:border-primary/40 transition-all duration-200 cursor-pointer"
                >
                  <item.icon className="h-6 w-6 text-foreground shrink-0 group-hover:scale-110 transition-transform duration-200" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[10.5px] font-bold uppercase text-foreground leading-tight group-hover:text-primary transition-colors">{item.title}</div>
                    <div className="text-[9.5px] text-foreground/70 mt-0.5 leading-tight line-clamp-2">{item.sub}</div>
                  </div>
                  <ArrowRight className="h-3 w-3 text-primary shrink-0 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-200" />
                </Link>
              ))}
            </div>
          </div>

          <div className="hidden md:grid w-[89%] max-w-[1140px] mx-auto -mt-7 relative z-10 bg-background rounded-md shadow-lg border border-border/70 grid-cols-5 md:divide-x divide-border">
            {trustItems.map((item) => (
              <Link
                key={item.title}
                to={item.href}
                className="group flex items-center justify-center gap-3 px-3 py-3 min-h-[58px] hover:bg-sand/60 transition-colors duration-200 cursor-pointer"
              >
                <item.icon className="h-7 w-7 text-foreground shrink-0 group-hover:scale-110 transition-transform duration-200" />
                <div>
                  <div className="text-[10px] font-bold uppercase text-foreground group-hover:text-primary transition-colors">{item.title}</div>
                  <div className="text-[10px] text-foreground/75 mt-0.5">{item.sub}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="w-[89%] max-w-[1140px] mx-auto pt-14 md:pt-28 pb-6 md:pb-12">
          <Link to="/about#approach" className="group block text-center mb-8 md:mb-14 cursor-pointer">
            <p className="text-[10px] tracking-[0.32em] uppercase text-muted-foreground mb-3 font-semibold group-hover:text-primary transition-colors">The LoftLoom Promise</p>
            <h2 className="font-display text-[28px] md:text-[48px] leading-tight text-foreground group-hover:text-primary transition-colors">Buying furniture online, without the guesswork.</h2>
            <p className="hidden md:block mt-3 text-[13px] italic text-foreground/70 max-w-[560px] mx-auto">Stop guessing what works together — our curated sets help you avoid expensive mistakes.</p>
          </Link>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6">
            {promiseItems.map((item, i) => (
              <Reveal key={item.title} index={i}>
              <Link
                to={item.href}
                className="group relative bg-sand border border-transparent p-5 md:p-8 flex gap-3 md:gap-5 items-start hover:border-primary/40 transition-all duration-200 cursor-pointer"
              >
                <div className="shrink-0 h-10 w-10 flex items-center justify-center">
                  <item.icon className="h-9 w-9 text-foreground" />
                </div>
                <div className="flex-1">
                  <div className="text-[13px] font-bold text-foreground mb-1">{item.title}</div>
                  <p className="text-[12px] text-foreground/75 leading-relaxed">{item.sub}</p>
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-primary shrink-0 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-200" />
              </Link>
              </Reveal>
            ))}
          </div>

        </section>


        <section className="w-[89%] max-w-[1140px] mx-auto py-10 md:py-16">
          <SectionTitle title="Shop by Room" href="/categories" action="View all rooms" />
          <div className="flex md:grid md:grid-cols-5 gap-3 md:gap-5 overflow-x-auto md:overflow-visible snap-x snap-mandatory scrollbar-hide -mx-[5.5%] md:mx-0 px-[5.5%] md:px-0">
            {rooms.map((room, index) => (
              <RoomMotionCard key={room.label} room={room} fallbackImage={roomLiving} index={index} />
            ))}
          </div>
        </section>

        <ShopByPrice />

        {/* Get the Look — shoppable room scene teaser */}
        <section className="py-10 md:py-20">
          <div className="w-[89%] max-w-[1140px] mx-auto">
            <SectionTitle title="Get the Look" href="/get-the-look" action="Explore all rooms" />
          </div>
          <Link to="/get-the-look" className="group relative block w-full overflow-hidden" aria-label="Get the look — warm minimalist living room">
            <img
              src="/journal/28-hero.webp"
              alt="Warm minimalist living room with walnut cabinet, bouclé sofa and travertine coffee table"
              loading="lazy"
              className="w-full h-[300px] md:h-[620px] object-cover group-hover:scale-[1.02] transition-transform duration-700 ease-out"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-foreground/10 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-16">
              <p className="text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-primary-foreground/70 font-semibold mb-1.5">
                Shoppable Scene
              </p>
              <h2 className="font-display text-2xl md:text-5xl text-primary-foreground tracking-[0.02em]">
                Warm Minimalist Living Room
              </h2>
              <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase font-bold text-primary-foreground mt-2">
                Shop the look
                <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform duration-300" />
              </span>
            </div>
          </Link>
        </section>

        {/* Style Quiz — personalised room board */}
        <section className="w-[89%] max-w-[1140px] mx-auto pt-2 pb-10 md:pt-0 md:pb-16">
          <Link
            to="/style-quiz"
            className="group flex flex-col md:flex-row md:items-center md:justify-between gap-4 border border-border bg-card px-6 py-8 md:px-16 md:py-14 hover:border-primary transition-colors"
            aria-label="Take the style quiz to build a personalised room board"
          >
            <div>
              <p className="text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-muted-foreground font-semibold mb-1.5">
                One Minute, Five Questions
              </p>
              <h2 className="font-display text-xl md:text-3xl text-foreground tracking-[0.02em]">
                Not sure where to start? Take the style quiz.
              </h2>
              <p className="text-sm text-muted-foreground mt-2 max-w-xl">
                Tell us the room, palette and mood you're after and we'll assemble a
                room board from pieces in stock today.
              </p>
            </div>
            <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase font-bold text-foreground group-hover:text-primary transition-colors shrink-0">
              Start the quiz
              <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform duration-300" />
            </span>
          </Link>
        </section>


        <EverydayFinds />

        <HomeProducts />

        <SitHappensSection />


        <section className="w-[89%] max-w-[1140px] mx-auto py-10 md:py-16">
          <SectionTitle title="Design Inspiration" href="/ideas" action="Browse design ideas" />
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-3">
            {looks.map((look, i) => (
              <Reveal key={i} index={i}>
              <Link to={look.href} className="block overflow-hidden">
                <img src={look.img} alt={look.alt} className="w-full h-[120px] md:h-[200px] object-cover hover:scale-[1.03] transition-transform duration-500" loading="lazy" />
              </Link>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="w-[89%] max-w-[1140px] mx-auto py-10 md:py-16 grid md:grid-cols-[175px_1fr] gap-5 md:items-center">
          <div className="hidden md:block">
            <p className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-1.5">WHY CHOOSE</p>
            <h2 className="font-display text-[30px] md:text-[24px] leading-none uppercase text-foreground">LoftLoom<br /><span className="text-[16px] md:text-[15px] tracking-[0.16em] font-sans font-bold">Interior</span></h2>
            <div className="h-px w-10 bg-primary my-2" />
            <p className="text-[13px] md:text-[12px] text-foreground/75 leading-relaxed max-w-[260px]">Thoughtful design, curated with care. For homes that feel as good as they look.</p>
            <Link to="/about" className="mt-2 inline-flex items-center gap-2 text-[10px] tracking-[0.14em] uppercase font-bold text-foreground hover:text-primary">Learn more about our design philosophy <ArrowRight className="h-3 w-3" /></Link>
          </div>
          <div className="bg-background border border-border/60 rounded-md shadow-sm grid grid-cols-3 md:grid-cols-5">
            {whyItems.map((item, i) => (
              <Reveal key={item.title} index={i}>
              <Link
                to={item.href}
                className={`group text-center px-2 md:px-3 py-3 md:py-3 min-h-[86px] md:min-h-[88px] flex flex-col items-center justify-center hover:bg-sand/60 transition-colors duration-200 cursor-pointer ${i === whyItems.length - 1 ? "col-span-1 md:col-span-1" : ""} ${i === whyItems.length - 2 ? "" : ""}`}
              >
                <item.icon className="h-7 w-7 md:h-7 md:w-7 text-foreground mb-1 group-hover:scale-110 transition-transform duration-200" />
                <div className="text-[11px] md:text-[12px] font-bold text-foreground mb-0.5 group-hover:text-primary transition-colors leading-tight">{item.title}</div>
                <div className="text-[9px] md:text-[10px] text-foreground/70 leading-tight max-w-[120px]">{item.sub}</div>

              </Link>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="w-[89%] max-w-[1040px] mx-auto py-10 md:py-16">
          <Link to="/design-services" className="block text-center mb-8 md:mb-12 cursor-pointer"><h2 className="text-[24px] md:text-[38px] font-display text-foreground hover:text-primary transition-colors">How It Works</h2></Link>
          <div className="flex md:grid md:grid-cols-4 gap-3 md:gap-y-5 overflow-x-auto md:overflow-visible snap-x snap-mandatory scrollbar-hide -mx-[5.5%] md:mx-0 px-[5.5%] md:px-0 relative">
            {howSteps.map((step, i) => (
              <Reveal key={step.n} index={i} className="shrink-0 w-[42%] md:w-auto snap-start">
              <Link
                to={step.href}
                className="group relative block text-center px-2 md:px-4 cursor-pointer"
              >
                <div className="text-[11px] font-bold text-foreground mb-1">{step.n}</div>
                <div className="mx-auto h-12 w-12 md:h-12 md:w-12 rounded-full bg-background border border-border shadow-md flex items-center justify-center mb-1.5 relative z-10 group-hover:border-primary group-hover:shadow-lg group-hover:-translate-y-0.5 transition-all duration-200">
                  <step.icon className="h-5 w-5 md:h-5 md:w-5 text-foreground group-hover:scale-110 transition-transform duration-200" />
                </div>
                {i < howSteps.length - 1 && <div className="hidden md:block absolute top-[34px] left-[calc(50%+38px)] right-[calc(-50%+38px)] border-t border-dashed border-border" />}
                <div className="text-[12px] md:text-[12px] font-bold text-foreground leading-tight group-hover:text-primary transition-colors">{step.title}</div>
                <div className="text-[11px] md:text-[11px] text-foreground/70 leading-snug max-w-[150px] mx-auto mt-0.5 line-clamp-2 md:line-clamp-none">{step.sub}</div>
              </Link>
              </Reveal>
            ))}

          </div>
        </section>

        {realReviews.length > 0 && (
        <section className="w-[89%] max-w-[1140px] mx-auto py-10 md:py-16">
          <Link to="/about#stats" className="block text-center mb-3 md:mb-2 cursor-pointer"><h2 className="text-[13px] md:text-[13px] tracking-[0.18em] uppercase font-display font-bold text-foreground hover:text-primary transition-colors">Loved by Real Homes</h2></Link>
          <div className="flex md:grid md:grid-cols-3 gap-3 md:gap-3 overflow-x-auto md:overflow-visible snap-x snap-mandatory scrollbar-hide -mx-[5.5%] md:mx-0 px-[5.5%] md:px-0">
            {realReviews.map((r, idx) => (
              <Reveal key={r.id} index={idx} className="shrink-0 w-[82%] md:w-auto snap-start">
              <Link
                to={`/product/${r.handle}#pdp-reviews`}
                className="group bg-sand rounded-md overflow-hidden flex md:flex-row md:min-h-[88px] hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer h-full"
              >
                <img src={reviewImages[idx % reviewImages.length]} alt="" className="w-[96px] md:w-[108px] h-auto object-cover group-hover:scale-105 transition-transform duration-500 shrink-0" loading="lazy" />
                <div className="p-3 md:p-3 flex-1 min-w-0">
                  <div className="flex gap-0.5 mb-1.5">{[...Array(r.rating)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 md:h-3.5 md:w-3.5 fill-primary text-primary" />)}</div>
                  <p className="text-[12px] md:text-[11px] text-foreground leading-snug mb-1.5 md:mb-1.5 line-clamp-3">“{r.quote}”</p>
                  <div className="text-[10px] md:text-[10px] font-semibold text-foreground/75 group-hover:text-primary transition-colors">— {r.name}</div>
                </div>
              </Link>
              </Reveal>
            ))}
          </div>
          <div className="flex items-center justify-center gap-2 mt-3 md:mt-2"><span className="h-2 w-2 rounded-full bg-primary" /><span className="h-2 w-2 rounded-full bg-border" /><span className="h-2 w-2 rounded-full bg-border" /></div>
        </section>
        )}

        <section className="w-[89%] max-w-[900px] mx-auto py-12 md:py-20">
          <Link to="/faq" className="group block text-center mb-8 md:mb-12 cursor-pointer">
            <p className="text-[10px] tracking-[0.32em] uppercase text-muted-foreground mb-3 font-semibold group-hover:text-primary transition-colors">Good to know</p>
            <h2 className="font-display text-[28px] md:text-[44px] leading-tight text-foreground group-hover:text-primary transition-colors">Quick answers before you buy.</h2>
          </Link>
          <Accordion type="single" collapsible className="w-full">
            {homeFaqs.map((f, i) => (
              <AccordionItem key={i} value={`home-faq-${i}`} className={i >= 4 ? "hidden md:block" : ""}>
                <AccordionTrigger className="text-left text-[14px] md:text-[15px] font-semibold text-foreground hover:text-primary hover:no-underline cursor-pointer transition-colors">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[13px] text-foreground/75 leading-relaxed">
                  {f.a}
                  <div className="mt-2">
                    <Link to={f.href} className="group inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-primary hover:underline">
                      Read full answer <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-4">
            <Link to="/faq" className="group inline-flex items-center gap-1.5 text-[11px] tracking-[0.14em] uppercase font-bold text-foreground hover:text-primary transition-colors">
              See all FAQs <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </section>






        <section className="w-[89%] max-w-[1140px] mx-auto pb-14 md:pb-24">
          <div className="relative overflow-hidden md:min-h-[220px]">
            <img src={newsletterBg} alt="" className="absolute inset-0 w-full h-full object-cover object-[70%_60%] scale-x-[-1]" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-b md:bg-gradient-to-r from-[#7a4a32]/20 from-0% via-[#7a4a32]/65 via-[40%] to-[#7a4a32] to-[60%]" />

            <div className="relative grid md:grid-cols-[1fr_1.05fr_1.15fr] items-center gap-4 md:gap-5 px-6 md:px-12 py-8 md:py-10">
              <Link to="/ideas" className="group block text-primary-foreground cursor-pointer">
                <h2 className="font-display text-[26px] md:text-[36px] leading-[0.95] md:leading-[0.88] mb-2 group-hover:opacity-90 transition-opacity">Your home.<br />Your story.</h2>
                <p className="text-[12px] md:text-[12px] leading-snug max-w-[320px]">New guides, room ideas and new arrivals, by email.</p>
              </Link>
              <div className="hidden md:block" />
              <div className="md:pr-[118px]">
                <form onSubmit={handleNewsletter} className="flex flex-col md:flex-row md:h-9 bg-transparent md:bg-background md:rounded-sm md:overflow-hidden md:shadow-sm gap-2 md:gap-0">
                  <label htmlFor="newsletter-email" className="sr-only">Email address</label>
                  <input id="newsletter-email" name="email" type="email" required value={newsletterEmail} onChange={(e) => setNewsletterEmail(e.target.value)} aria-label="Email address" placeholder="Your email address" className="min-w-0 flex-1 h-11 md:h-auto bg-background rounded-sm md:rounded-none px-4 text-[14px] md:text-[12px] text-foreground outline-none placeholder:text-muted-foreground" />
                  <button type="submit" aria-label="Subscribe to newsletter" className="h-11 md:h-auto bg-foreground text-background px-5 md:px-6 text-[11px] md:text-[10px] tracking-[0.16em] uppercase font-bold hover:bg-foreground/90 rounded-sm md:rounded-none">Subscribe</button>
                </form>
                <div className="hidden md:grid grid-cols-3 gap-2 text-primary-foreground mt-4 md:mt-2">

                  {[
                    { Icon: Star, t: "New Arrivals", s: "As They Land", href: "/category/new-arrivals" },
                    { Icon: Target, t: "New Ideas", s: "Room by Room", href: "/get-the-look" },
                    { Icon: Gem, t: "Design Inspiration", s: "Curated for You", href: "/ideas" },
                  ].map((b) => (
                    <Link key={b.t} to={b.href} className="group text-center min-w-0 rounded-sm px-1 py-1 hover:bg-background/10 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer">
                      <b.Icon className="h-5 w-5 md:h-4 md:w-4 mx-auto mb-1 stroke-[1.5] group-hover:scale-110 transition-transform duration-200" />
                      <div className="text-[10px] font-bold leading-tight">{b.t}</div>
                      <div className="text-[9px] leading-tight opacity-90">{b.s}</div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            <img src={vaseDecor} alt="" className="hidden md:block absolute right-0 bottom-0 h-full w-[118px] object-cover" loading="lazy" />
          </div>
        </section>


      </main>

      <Footer />
    </div>
  );
};

export default Index;